import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  SpinnerGap,
  Certificate,
  Users,
  Minus,
  Plus,
  Ticket,
  Eye,
  DownloadSimple,
  QrCode
} from '@phosphor-icons/react';
import { adminInternsAPI } from '../../services/api';
import LetterDocument from '../Intern/LetterDocument';

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyOffline = {
  letter_type: 'offer',
  name: '',
  college: '',
  phone: '',
  intern_code: '',
  role_slug: 'full-stack-developer',
  join_date: '',
  complete_date: '',
  issued_at: todayISO()
};

const prettyIssue = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const exportLettersExcel = (rows) => {
  const headers = ['Name', 'Phone', 'Intern ID', 'Scanner ID', 'Type', 'Role', 'Start date', 'End date', 'Issue date', 'College', 'Email', 'Weeks', 'Verify URL'];
  const lines = [
    headers.join(','),
    ...rows.map((item) => [
      item.intern_name,
      item.phone,
      item.intern_code,
      item.letter_code,
      item.type === 'completion' ? 'Completion' : 'Offer',
      item.role_title,
      prettyIssue(item.join_date),
      prettyIssue(item.complete_date),
      prettyIssue(item.issued_at),
      item.college,
      item.email,
      item.weeks_total || '',
      item.verify_url || ''
    ].map(csvCell).join(','))
  ];
  const blob = new Blob([`\ufeff${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `growmore-offline-letters-${todayISO()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const emptyCoupon = { code: '', type: 'percent', value: 10, max_uses: '', expires_at: '', role_slugs: [], plan_id: '' };

const dmy = (iso) => {
  if (!iso) return 'No expiry';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB');
};

const TABS = [
  { id: 'active', label: 'Active intern' },
  { id: 'certs', label: 'All certificates' }
];

const letterKind = (type) => (type === 'completion' ? 'Completion' : 'Offer');

const unsignedHref = (code) => `/verify/${code}?unsigned=1`;

const UnsignedDownload = ({ code, label = 'Download without stamp/sign' }) => (
  <a
    href={unsignedHref(code)}
    target="_blank"
    rel="noreferrer"
    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 whitespace-nowrap"
  >
    <DownloadSimple className="w-3.5 h-3.5" />
    {label}
  </a>
);

const sampleLetter = (type, form) => {
  const values = {
    name: 'Aarav Patil',
    role: 'Full Stack Developer',
    weeks: '4',
    join_date: '1 April 2026',
    complete_date: '30 April 2026',
    college: 'Government College of Engineering, Satara'
  };
  const swap = (text) => String(text || '').replace(/\{\{(\w+)\}\}/g, (_, key) => {
    const map = {
      name: values.name,
      candidate_name: values.name,
      role: values.role,
      internship_role: values.role,
      weeks: values.weeks,
      join_date: values.join_date,
      start_date: values.join_date,
      complete_date: values.complete_date,
      end_date: values.complete_date,
      college: values.college,
      certificate_no: type === 'offer' ? 'GMIS-OFR-DEMO' : 'GMIS-CMP-DEMO',
      issue_date: '1 Apr 2026',
      authorized_person: form.signatory || 'Vivek Kalaskar',
      letter_code: type === 'offer' ? 'GMIS-OFR-DEMO' : 'GMIS-CMP-DEMO'
    };
    return map[key] ?? '';
  });
  return {
    type,
    intern_name: values.name,
    intern_code: 'GMIS-INT-DEMO',
    letter_code: type === 'offer' ? 'GMIS-OFR-DEMO' : 'GMIS-CMP-DEMO',
    college: values.college,
    email: 'aarav.patil@example.com',
    phone: '9876543210',
    role_title: values.role,
    weeks_total: 4,
    join_date: '2026-04-01',
    complete_date: '2026-04-30',
    issued_at: '2026-04-01',
    qr_url: 'https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=' + encodeURIComponent('https://growmoreitservices.com/verify/GMIS-CMP-DEMO'),
    verify_url: 'https://growmoreitservices.com/verify/GMIS-CMP-DEMO',
    description: 'Weekly tasks completed through the intern portal.',
    company: {
      name: 'Growmore IT Services',
      address: 'Mangalmurti Apartment, More Colony, Godoli, Satara',
      email: 'info@growmoreitservices.com',
      phone: '+91 7350695792',
      signatory: form.signatory || 'Vivek Kalaskar',
      signatory_title: form.signatory_title || 'Founder'
    },
    format: {
      heading: swap(form.heading),
      subject: swap(form.subject),
      body: swap(form.body),
      footer_note: swap(form.footer_note),
      stamp_signed: form.stamp_signed,
      stamp_unsigned: form.stamp_unsigned,
      show_seal: form.show_seal !== false,
      show_ribbon: !!form.show_ribbon,
      show_badge: form.show_badge !== false,
      stamp_svg: form.stamp_svg,
      signature_svg: form.signature_svg
    }
  };
};

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25];

const WordDesk = ({ letter, zoom, onZoom }) => {
  const slotRef = useRef(null);
  const [fitScale, setFitScale] = useState(0.6);
  useEffect(() => {
    const el = slotRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.clientWidth - 56;
      const h = el.clientHeight - 56;
      if (w < 80 || h < 80) return;
      setFitScale(Math.max(0.3, Math.min(w / 794, h / 1123, 1.15)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const scale = zoom === 'fit' ? fitScale : zoom;
  const bump = (dir) => {
    const current = zoom === 'fit' ? fitScale : zoom;
    const next = ZOOM_STEPS.reduce((best, step) => {
      if (dir > 0 && step > current + 0.01) return best == null ? step : Math.min(best, step);
      if (dir < 0 && step < current - 0.01) return best == null ? step : Math.max(best, step);
      return best;
    }, null);
    onZoom(next ?? (dir > 0 ? Math.min(1.5, current + 0.1) : Math.max(0.4, current - 0.1)));
  };
  return (
    <div className="word-desk-wrap">
      <div className="word-zoombar">
        <button type="button" className="word-zoom-btn" onClick={() => bump(-1)} aria-label="Zoom out">−</button>
        {[['fit', 'Fit'], [0.5, '50%'], [0.75, '75%'], [1, '100%']].map(([value, label]) => (
          <button
            key={String(value)}
            type="button"
            className={`word-zoom-btn ${zoom === value ? 'is-on' : ''}`}
            onClick={() => onZoom(value)}
          >
            {label}
          </button>
        ))}
        <button type="button" className="word-zoom-btn" onClick={() => bump(1)} aria-label="Zoom in">+</button>
        <span className="word-zoom-label">{Math.round(scale * 100)}%</span>
      </div>
      <div ref={slotRef} className="word-desk">
        <div className="word-page-wrap" style={{ width: 794 * scale, height: 1123 * scale }}>
          <div style={{ width: 794, height: 1123, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
            <LetterDocument letter={letter} />
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminInterns = ({ section = 'interns' }) => {
  const [view, setView] = useState('active');
  const [interns, setInterns] = useState([]);
  const [letters, setLetters] = useState([]);
  const [roles, setRoles] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');
  const [offline, setOffline] = useState(emptyOffline);
  const [scanStep, setScanStep] = useState(false);
  const [scan, setScan] = useState(null);
  const [formatForm, setFormatForm] = useState({});
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState(emptyCoupon);
  const [formatType, setFormatType] = useState('offer');
  const [paperZoom, setPaperZoom] = useState('fit');

  const load = async () => {
    const [{ data }, plansRes, formatRes, couponRes] = await Promise.all([
      adminInternsAPI.list(),
      adminInternsAPI.plans(),
      adminInternsAPI.formats().catch(() => ({ data: { formats: [] } })),
      adminInternsAPI.coupons().catch(() => ({ data: { coupons: [] } }))
    ]);
    setInterns(data.interns || []);
    setLetters(data.letters || []);
    setRoles(data.roles || []);
    setPlans(plansRes.data.plans || []);
    const next = {};
    (formatRes.data.formats || []).forEach((item) => {
      next[item.type] = { ...item };
    });
    setFormatForm(next);
    setCoupons(couponRes.data.coupons || []);
    setLoading(false);
  };

  useEffect(() => {
    load().catch(() => setLoading(false));
  }, []);

  const act = async (fn, ok) => {
    try {
      const { data } = await fn();
      setNotice(ok || data.message || 'Saved.');
      if (data.password) setNotice(`Email ${data.email || ''} · password ${data.password}`);
      await load();
      return data;
    } catch (err) {
      setNotice(err.response?.data?.message || 'Action failed.');
      return null;
    }
  };

  const paidInterns = useMemo(
    () => interns.filter((item) => item.origin !== 'offline' && item.payment_status === 'paid'),
    [interns]
  );
  const onlineLetters = useMemo(
    () => letters.filter((item) => item.origin !== 'offline'),
    [letters]
  );
  const offlineLetters = useMemo(
    () => letters.filter((item) => item.origin === 'offline'),
    [letters]
  );

  const offlinePayload = () => {
    const role = roles.find((item) => item.slug === offline.role_slug);
    return {
      letter_type: offline.letter_type,
      name: scan?.name || offline.name,
      college: scan?.college || offline.college,
      phone: scan?.phone || offline.phone,
      intern_code: scan?.intern_code || offline.intern_code || undefined,
      letter_code: scan?.letter_code || undefined,
      role_slug: offline.role_slug,
      role_title: role?.title || '',
      join_date: scan?.join_date || offline.join_date,
      complete_date: scan?.complete_date || offline.complete_date,
      issued_at: scan?.issued_at || offline.issued_at || todayISO()
    };
  };

  const reviewOffline = async (event) => {
    event.preventDefault();
    const data = await act(() => adminInternsAPI.previewOffline(offlinePayload()), 'Scanner preview ready. Check the details, then accept.');
    if (data?.scan) {
      setScan(data.scan);
      setScanStep(true);
    }
  };

  const acceptOffline = async () => {
    const data = await act(() => adminInternsAPI.createOffline(offlinePayload()));
    if (data) {
      setOffline({ ...emptyOffline, issued_at: todayISO() });
      setScan(null);
      setScanStep(false);
    }
  };

  const bumpPrice = async (plan, delta) => {
    const price = Math.max(0, Number(plan.price || 0) + delta);
    await act(() => adminInternsAPI.updatePlan(plan.id, { price }), `₹${price.toLocaleString('en-IN')} saved.`);
  };

  const saveCoupon = async (event) => {
    event.preventDefault();
    if (!couponForm.role_slugs.length) {
      setNotice('Select at least one internship for this coupon.');
      return;
    }
    const payload = {
      code: couponForm.code,
      type: couponForm.type,
      value: Number(couponForm.value),
      role_slugs: couponForm.role_slugs,
      plan_id: couponForm.plan_id ? Number(couponForm.plan_id) : null,
      max_uses: couponForm.max_uses ? Number(couponForm.max_uses) : null,
      expires_at: couponForm.expires_at || null,
      is_active: true
    };
    const data = await act(() => adminInternsAPI.createCoupon(payload), 'Coupon saved.');
    if (data) setCouponForm(emptyCoupon);
  };

  const toggleCouponRole = (slug) => {
    setCouponForm((prev) => {
      const has = prev.role_slugs.includes(slug);
      return {
        ...prev,
        role_slugs: has ? prev.role_slugs.filter((item) => item !== slug) : [...prev.role_slugs, slug]
      };
    });
  };

  const saveFormat = async (type) => {
    const payload = formatForm[type];
    if (!payload) return;
    await act(() => adminInternsAPI.updateFormat(type, payload), `${type} format saved.`);
  };

  if (loading) {
    return (
      <p className="text-gray-500 flex items-center gap-2">
        <SpinnerGap className="w-5 h-5 animate-spin" /> Loading...
      </p>
    );
  }

  const internCard = (intern) => {
    const submitted = intern.weeks_submitted || 0;
    const reviewed = intern.weeks_reviewed || 0;
    const total = intern.weeks_total || 0;
    const pct = total ? Math.round((submitted / total) * 100) : 0;
    return (
      <article key={intern.id} className="rounded-2xl sm:rounded-3xl bg-white border border-gray-100 p-4 sm:p-5 shadow-soft space-y-4">
        <div className="flex flex-wrap justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase text-brand-600">{intern.role_title}</p>
            <h3 className="font-display text-xl font-extrabold text-gray-900">{intern.name}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{intern.plan_name || 'Internship'} · {total} weeks</p>
          </div>
          <div className="flex flex-wrap gap-2 items-start">
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-50 text-gray-600 capitalize">{intern.status}</span>
            {intern.payment_status !== 'paid' && intern.origin !== 'offline' && (
              <button type="button" onClick={() => act(() => adminInternsAPI.markPaid(intern.id))} className="px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-sm font-semibold">Mark paid</button>
            )}
            <Link
              to={`/admin/interns/${intern.id}`}
              className="px-3 py-2 rounded-xl bg-brand-600 text-white text-sm font-semibold inline-flex items-center gap-1.5"
            >
              <Eye className="w-4 h-4" /> View
            </Link>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
          {[
            ['Login email', intern.email],
            ['Phone', intern.phone],
            ['Intern ID', intern.intern_code],
            ['College', intern.college],
            ['Date of birth', intern.date_of_birth || '—'],
            ['Join date', intern.started_at || '—']
          ].map(([label, value]) => (
            <div key={label} className="rounded-2xl bg-slate-50 px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
              <p className="font-semibold text-gray-800 break-all">{value || '—'}</p>
            </div>
          ))}
        </div>
        <div>
          <div className="flex justify-between text-xs font-semibold text-gray-500 mb-1.5">
            <span>{submitted}/{total} weeks submitted · {reviewed} reviewed</span>
            <span>{pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
        {(intern.letters || []).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {intern.letters.map((letter) => (
              <UnsignedDownload
                key={letter.letter_code}
                code={letter.letter_code}
                label={`${letterKind(letter.type)} without stamp/sign`}
              />
            ))}
          </div>
        )}
      </article>
    );
  };

  return (
    <div className={section === 'formats' ? 'h-full min-h-0 flex flex-col' : 'space-y-5'}>
      {section === 'interns' && (
        <div className="flex flex-wrap gap-2">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setView(tab.id)}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold border ${
                view === tab.id ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-700 border-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {notice && <p className="text-sm bg-brand-50 text-brand-800 border border-brand-100 rounded-xl px-4 py-3">{notice}</p>}

      {section === 'interns' && view === 'active' && (
        <section className="space-y-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Users className="w-4 h-4" />
            {paidInterns.length} paid internships
          </div>
          {paidInterns.map((intern) => internCard(intern))}
          {paidInterns.length === 0 && <p className="text-gray-500">No paid internships yet.</p>}
        </section>
      )}

      {section === 'interns' && view === 'certs' && (
        <section>
          <div className="flex items-center gap-2 mb-4 text-sm text-gray-500">
            <Certificate className="w-4 h-4" />
            {onlineLetters.length} online certificates issued
          </div>
          <div className="overflow-x-auto rounded-3xl bg-white border border-gray-100 shadow-soft">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold">Letter ID</th>
                  <th className="px-4 py-3 font-semibold">Issue date</th>
                  <th className="px-4 py-3 font-semibold">Download</th>
                </tr>
              </thead>
              <tbody>
                {onlineLetters.map((item) => (
                  <tr key={item.letter_code} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-semibold text-gray-900">{item.intern_name}</td>
                    <td className="px-4 py-3">{letterKind(item.type)}</td>
                    <td className="px-4 py-3">
                      <Link className="font-semibold text-brand-600" to={`/verify/${item.letter_code}`} target="_blank">
                        {item.letter_code}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{prettyIssue(item.issued_at)}</td>
                    <td className="px-4 py-3">
                      <UnsignedDownload code={item.letter_code} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {onlineLetters.length === 0 && <p className="text-gray-500 mt-3">No online certificates issued yet.</p>}
        </section>
      )}

      {section === 'certificate' && (
        <section className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-gray-900">Offline letters</h1>
              <p className="text-sm text-gray-500 mt-1">Creates a letter only — no intern login. One student keeps one intern ID for offer and completion.</p>
              <Link to="/admin/interns" className="inline-block mt-2 text-sm font-semibold text-brand-600 hover:text-brand-700">
                Open paid intern list →
              </Link>
            </div>
            {offlineLetters.length > 0 && (
              <button type="button" onClick={() => exportLettersExcel(offlineLetters)} className="px-4 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2">
                <DownloadSimple className="w-4 h-4" /> Export Excel
              </button>
            )}
          </div>

          <form onSubmit={reviewOffline} className="rounded-3xl bg-white border border-gray-100 p-5 shadow-soft space-y-5">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-brand-600 mb-2">Letter type</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { id: 'offer', title: 'Offer letter', hint: 'Issued when the internship starts.' },
                  { id: 'completion', title: 'Completion letter', hint: 'Issued when the internship ends.' }
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => { setOffline((p) => ({ ...p, letter_type: item.id })); setScanStep(false); setScan(null); }}
                    className={`text-left rounded-2xl border px-4 py-3 ${
                      offline.letter_type === item.id ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-100' : 'border-gray-100 hover:border-brand-200'
                    }`}
                  >
                    <p className="font-display font-bold text-gray-900">{item.title}</p>
                    <p className="text-xs text-gray-500 mt-1">{item.hint}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-xs font-semibold text-gray-500">Full name
                <input className="input-field mt-1" required value={offline.name} onChange={(e) => setOffline((p) => ({ ...p, name: e.target.value }))} />
              </label>
              <label className="text-xs font-semibold text-gray-500">Mobile number
                <input className="input-field mt-1" required value={offline.phone} onChange={(e) => setOffline((p) => ({ ...p, phone: e.target.value }))} />
              </label>
              <label className="text-xs font-semibold text-gray-500">College
                <input className="input-field mt-1" value={offline.college} onChange={(e) => setOffline((p) => ({ ...p, college: e.target.value }))} />
              </label>
              <label className="text-xs font-semibold text-gray-500">Internship role
                <select className="input-field mt-1" value={offline.role_slug} onChange={(e) => setOffline((p) => ({ ...p, role_slug: e.target.value }))}>
                  {roles.map((role) => (
                    <option key={role.slug} value={role.slug}>{role.title}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-semibold text-gray-500">Start date
                <input className="input-field mt-1" required type="date" value={offline.join_date} onChange={(e) => setOffline((p) => ({ ...p, join_date: e.target.value }))} />
              </label>
              <label className="text-xs font-semibold text-gray-500">End date
                <input className="input-field mt-1" required type="date" value={offline.complete_date} onChange={(e) => setOffline((p) => ({ ...p, complete_date: e.target.value }))} />
              </label>
              <label className="text-xs font-semibold text-gray-500">Issue date
                <input className="input-field mt-1" required type="date" value={offline.issued_at} onChange={(e) => setOffline((p) => ({ ...p, issued_at: e.target.value }))} />
              </label>
              <label className="text-xs font-semibold text-gray-500">Existing intern ID (if this student already has a letter)
                <input className="input-field mt-1" placeholder="GMIS-INT-… leave blank for a new ID" value={offline.intern_code} onChange={(e) => setOffline((p) => ({ ...p, intern_code: e.target.value }))} />
              </label>
            </div>
            <p className="text-xs text-gray-500">Intern ID, scanner ID and weeks are created automatically. Same name + mobile reuses the same intern ID.</p>
            <button type="submit" className="btn-primary text-white inline-flex items-center gap-2">
              <QrCode className="w-4 h-4" /> Review scanner
            </button>
          </form>

          {scanStep && scan && (
            <div className="rounded-3xl bg-white border border-brand-100 p-5 shadow-soft space-y-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-brand-600">Scanner review</p>
                <h2 className="font-display text-xl font-extrabold text-gray-900">This is what a QR scan will show</h2>
                <p className="text-sm text-gray-500 mt-1">Change anything that is wrong, then accept to create the letter.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  ['name', 'Name'],
                  ['phone', 'Mobile number'],
                  ['college', 'College'],
                  ['role_title', 'Role'],
                  ['intern_code', 'Intern ID'],
                  ['letter_code', 'Scanner ID'],
                  ['join_date', 'Start date'],
                  ['complete_date', 'End date'],
                  ['issued_at', 'Issue date'],
                  ['weeks_total', 'Weeks']
                ].map(([key, label]) => (
                  <label key={key} className="text-xs font-semibold text-gray-500">
                    {label}
                    <input
                      className="input-field mt-1"
                      readOnly={key === 'intern_code' || key === 'letter_code' || key === 'weeks_total'}
                      type={['join_date', 'complete_date', 'issued_at'].includes(key) ? 'date' : 'text'}
                      value={scan[key] || ''}
                      onChange={(e) => setScan((p) => ({ ...p, [key]: e.target.value }))}
                    />
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={acceptOffline} className="btn-primary text-white">Accept & create letter</button>
                <button type="button" onClick={() => { setScanStep(false); setScan(null); }} className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold">Back</button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-500">
            <p className="inline-flex items-center gap-2"><Certificate className="w-4 h-4" /> {offlineLetters.length} offline letters</p>
          </div>
          <div className="overflow-x-auto rounded-3xl bg-white border border-gray-100 shadow-soft">
            <table className="w-full text-sm min-w-[1040px]">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Number</th>
                  <th className="px-4 py-3 font-semibold">Intern ID</th>
                  <th className="px-4 py-3 font-semibold">Scanner ID</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold">Start</th>
                  <th className="px-4 py-3 font-semibold">End</th>
                  <th className="px-4 py-3 font-semibold">Issue date</th>
                  <th className="px-4 py-3 font-semibold">Download</th>
                </tr>
              </thead>
              <tbody>
                {offlineLetters.map((item) => (
                  <tr key={item.letter_code} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-semibold text-gray-900">{item.intern_name}</td>
                    <td className="px-4 py-3 text-gray-600">{item.phone || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{item.intern_code}</td>
                    <td className="px-4 py-3">
                      <Link className="font-semibold text-brand-600" to={`/verify/${item.letter_code}`} target="_blank">
                        {item.letter_code}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{letterKind(item.type)}</td>
                    <td className="px-4 py-3 text-gray-600">{prettyIssue(item.join_date)}</td>
                    <td className="px-4 py-3 text-gray-600">{prettyIssue(item.complete_date)}</td>
                    <td className="px-4 py-3 text-gray-600">{prettyIssue(item.issued_at)}</td>
                    <td className="px-4 py-3">
                      <UnsignedDownload code={item.letter_code} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {offlineLetters.length === 0 && <p className="text-gray-500">No offline letters yet.</p>}
        </section>
      )}

      {section === 'plans' && (
        <section className="space-y-6">
          <div>
            <h1 className="text-display text-2xl sm:text-3xl font-extrabold text-gray-900">Internship plans</h1>
            <p className="text-sm text-gray-500 mt-1">Change amounts here. The apply form uses these prices. Coupons sit on the same page.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {plans.map((plan) => (
              <article key={plan.id} className={`rounded-3xl border p-5 bg-white shadow-soft ${plan.is_active ? 'border-gray-100' : 'border-dashed opacity-70'}`}>
                <p className="text-xs uppercase tracking-wide text-gray-400">{plan.months} month{plan.months > 1 ? 's' : ''} · {plan.weeks} weeks</p>
                <p className="font-display font-extrabold text-xl mt-1">{plan.name}</p>
                <p className="text-3xl font-extrabold text-brand-700 mt-2">₹{Number(plan.price).toLocaleString('en-IN')}</p>
                <p className="text-sm text-gray-500 mt-1">{plan.description || 'No note'}</p>
                <div className="flex flex-wrap items-center gap-2 mt-4">
                  <button type="button" onClick={() => bumpPrice(plan, -100)} className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold inline-flex items-center gap-1">
                    <Minus className="w-4 h-4" /> − ₹100
                  </button>
                  <button type="button" onClick={() => bumpPrice(plan, 100)} className="px-3 py-2 rounded-xl bg-brand-50 text-brand-700 text-sm font-semibold inline-flex items-center gap-1">
                    <Plus className="w-4 h-4" /> + ₹100
                  </button>
                  <input
                    className="input-field w-28 py-2"
                    type="number"
                    min="0"
                    value={plan.price}
                    onChange={(e) => setPlans((prev) => prev.map((row) => (row.id === plan.id ? { ...row, price: e.target.value } : row)))}
                    onBlur={(e) => act(() => adminInternsAPI.updatePlan(plan.id, { price: Number(e.target.value) || 0 }), 'Amount saved.')}
                  />
                </div>
                <div className="flex gap-3 mt-4">
                  <button type="button" onClick={() => act(() => adminInternsAPI.updatePlan(plan.id, { is_active: !plan.is_active }), plan.is_active ? 'Plan hidden.' : 'Plan shown.')} className="text-sm font-semibold text-gray-500">{plan.is_active ? 'Hide' : 'Show'}</button>
                  <button type="button" onClick={() => act(() => adminInternsAPI.deletePlan(plan.id), 'Plan deleted.')} className="text-sm font-semibold text-red-500">Delete</button>
                </div>
              </article>
            ))}
          </div>

          <section className="rounded-3xl bg-white border border-gray-100 p-5 shadow-soft">
            <div className="flex items-center gap-2 mb-1">
              <Ticket className="w-5 h-5 text-brand-600" weight="duotone" />
              <h2 className="font-display text-xl font-extrabold">Discount / coupon codes</h2>
            </div>
            <p className="text-sm text-gray-500 mb-5">A coupon works only on the internships you tick. It will not apply to the rest.</p>
            <form onSubmit={saveCoupon} className="space-y-4 mb-6">
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block text-xs font-semibold text-gray-500">
                  Coupon code
                  <input className="input-field mt-1 uppercase" placeholder="SUMMER20" value={couponForm.code} onChange={(e) => setCouponForm((p) => ({ ...p, code: e.target.value }))} required />
                </label>
                <label className="block text-xs font-semibold text-gray-500">
                  Discount type
                  <select className="input-field mt-1" value={couponForm.type} onChange={(e) => setCouponForm((p) => ({ ...p, type: e.target.value }))}>
                    <option value="percent">Percent %</option>
                    <option value="flat">Flat ₹ off</option>
                  </select>
                </label>
                <label className="block text-xs font-semibold text-gray-500">
                  {couponForm.type === 'flat' ? 'Amount off (₹)' : 'Percent off'}
                  <input className="input-field mt-1" type="number" min="1" value={couponForm.value} onChange={(e) => setCouponForm((p) => ({ ...p, value: e.target.value }))} required />
                </label>
                <label className="block text-xs font-semibold text-gray-500">
                  Plan (optional)
                  <select className="input-field mt-1" value={couponForm.plan_id} onChange={(e) => setCouponForm((p) => ({ ...p, plan_id: e.target.value }))}>
                    <option value="">Any duration for the selected internships</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>{plan.name} · ₹{Number(plan.price).toLocaleString('en-IN')}</option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs font-semibold text-gray-500">
                  Max uses (optional)
                  <input className="input-field mt-1" type="number" min="1" placeholder="Unlimited" value={couponForm.max_uses} onChange={(e) => setCouponForm((p) => ({ ...p, max_uses: e.target.value }))} />
                </label>
                <label className="block text-xs font-semibold text-gray-500">
                  Expiry date (dd-mm-yyyy)
                  <input className="input-field mt-1" type="date" value={couponForm.expires_at} onChange={(e) => setCouponForm((p) => ({ ...p, expires_at: e.target.value }))} />
                </label>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">Applies to these internships</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-56 overflow-y-auto rounded-2xl border border-gray-100 p-3">
                  {roles.map((role) => {
                    const on = couponForm.role_slugs.includes(role.slug);
                    return (
                      <label key={role.slug} className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-sm cursor-pointer ${on ? 'border-brand-300 bg-brand-50' : 'border-gray-100 bg-white'}`}>
                        <input type="checkbox" checked={on} onChange={() => toggleCouponRole(role.slug)} className="mt-0.5" />
                        <span className="font-semibold text-gray-800 leading-snug">{role.title}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <button type="submit" className="btn-primary text-white text-sm py-2.5 px-5">Add coupon</button>
            </form>
            {coupons.length === 0 ? (
              <p className="text-sm text-gray-500">No coupons yet. Tick internships, then add a code.</p>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-gray-100">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Code</th>
                      <th className="px-4 py-3">Internships</th>
                      <th className="px-4 py-3">Discount</th>
                      <th className="px-4 py-3">Uses</th>
                      <th className="px-4 py-3">Expires</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.map((item) => {
                      const names = (item.role_slugs || [])
                        .map((slug) => roles.find((role) => role.slug === slug)?.title || slug)
                        .join(', ');
                      const planName = plans.find((plan) => Number(plan.id) === Number(item.plan_id))?.name;
                      return (
                      <tr key={item.id} className="border-t border-gray-100">
                        <td className="px-4 py-3 font-bold tracking-wide">{item.code}</td>
                        <td className="px-4 py-3 text-gray-700 max-w-xs">
                          {names || '—'}
                          {planName ? <span className="block text-xs text-gray-400">{planName} only</span> : null}
                        </td>
                        <td className="px-4 py-3">{item.type === 'flat' ? `₹${item.value}` : `${item.value}%`}</td>
                        <td className="px-4 py-3">{item.used_count || 0}{item.max_uses ? ` / ${item.max_uses}` : ''}</td>
                        <td className="px-4 py-3 text-gray-500">{dmy(item.expires_at)}</td>
                        <td className="px-4 py-3">
                          <button type="button" onClick={() => act(() => adminInternsAPI.updateCoupon(item.id, { is_active: !item.is_active }))} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${item.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                            {item.is_active ? 'Active' : 'Off'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button type="button" onClick={() => act(() => adminInternsAPI.deleteCoupon(item.id), 'Coupon deleted.')} className="text-sm font-semibold text-red-500">Delete</button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </section>
      )}

      {section === 'formats' && (
        <section className="format-page">
          <div className="flex flex-wrap items-center justify-between gap-3 shrink-0">
            <div className="flex flex-wrap items-center gap-2">
              {['offer', 'completion'].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setFormatType(type)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold border capitalize ${
                    formatType === type ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-700 border-gray-200'
                  }`}
                >
                  {type} letter
                </button>
              ))}
            </div>
            <button type="button" onClick={() => saveFormat(formatType)} className="btn-primary text-white text-sm px-5">
              Save {formatType} format
            </button>
          </div>
          {(() => {
            const form = formatForm[formatType] || {};
            const setField = (key, value) => setFormatForm((p) => ({ ...p, [formatType]: { ...p[formatType], [key]: value } }));
            return (
              <div className="format-split">
                <div className="rounded-3xl bg-white border border-gray-100 p-4 space-y-3 shadow-soft format-editor">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Edit letter text</p>
                  <p className="text-[11px] text-gray-400 leading-5">Placeholders: {'{{candidate_name}} {{internship_role}} {{start_date}} {{end_date}} {{name}} {{role}}'}</p>
                  <input className="input-field font-letter" placeholder="Heading" value={form.heading || ''} onChange={(e) => setField('heading', e.target.value)} />
                  {formatType === 'offer' && (
                    <input className="input-field" placeholder="Subject line" value={form.subject || ''} onChange={(e) => setField('subject', e.target.value)} />
                  )}
                  <textarea className="input-field font-letter text-[15px] leading-7 min-h-[180px]" rows={8} placeholder="Body" value={form.body || ''} onChange={(e) => setField('body', e.target.value)} />
                  {formatType === 'offer' && (
                    <input className="input-field" placeholder="Footer note" value={form.footer_note || ''} onChange={(e) => setField('footer_note', e.target.value)} />
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <input className="input-field" placeholder="Signatory" value={form.signatory || ''} onChange={(e) => setField('signatory', e.target.value)} />
                    <input className="input-field" placeholder="Title" value={form.signatory_title || ''} onChange={(e) => setField('signatory_title', e.target.value)} />
                  </div>
                  <div className="pt-2 border-t border-gray-100 space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Stamp & Signature (Upload SVG / PNG)</p>
                    <div>
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span className="font-semibold">Company Stamp</span>
                        {form.stamp_svg && (
                          <button type="button" onClick={() => setField('stamp_svg', null)} className="text-red-500 hover:underline">
                            Remove
                          </button>
                        )}
                      </div>
                      <input
                        type="file"
                        accept=".svg,image/svg+xml,image/png,image/jpeg"
                        className="text-xs text-gray-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100 cursor-pointer"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          if (file.type === 'image/svg+xml' || file.name.endsWith('.svg')) {
                            reader.onload = (ev) => setField('stamp_svg', ev.target.result);
                            reader.readAsText(file);
                          } else {
                            reader.onload = (ev) => setField('stamp_svg', ev.target.result);
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span className="font-semibold">Authorized Signature</span>
                        {form.signature_svg && (
                          <button type="button" onClick={() => setField('signature_svg', null)} className="text-red-500 hover:underline">
                            Remove
                          </button>
                        )}
                      </div>
                      <input
                        type="file"
                        accept=".svg,image/svg+xml,image/png,image/jpeg"
                        className="text-xs text-gray-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100 cursor-pointer"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          if (file.type === 'image/svg+xml' || file.name.endsWith('.svg')) {
                            reader.onload = (ev) => setField('signature_svg', ev.target.result);
                            reader.readAsText(file);
                          } else {
                            reader.onload = (ev) => setField('signature_svg', ev.target.result);
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="format-preview">
                  <WordDesk letter={sampleLetter(formatType, form)} zoom={paperZoom} onZoom={setPaperZoom} />
                </div>
              </div>
            );
          })()}
        </section>
      )}
    </div>
  );
};

export default AdminInterns;
